import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { RouteReuseStrategy } from '@angular/router';
import { IonicModule, IonicRouteStrategy } from '@ionic/angular';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { environment } from '../environments/environment';
import { ServiceWorkerModule } from '@angular/service-worker';
import { Insomnia } from '@awesome-cordova-plugins/insomnia/ngx';
import { MappingsModule } from 'src/app/nav-processing/mappings/mappings.module';
import { BatteryStatus } from '@awesome-cordova-plugins/battery-status/ngx';
import { AndroidFullScreen } from '@awesome-cordova-plugins/android-full-screen/ngx';
import { AuthModule } from './auth/auth.module';
import { getAuth, provideAuth, connectAuthEmulator } from '@angular/fire/auth';
import { getApp, initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { getFirestore, provideFirestore, connectFirestoreEmulator, initializeFirestore } from '@angular/fire/firestore';
import { getFunctions, provideFunctions, connectFunctionsEmulator } from '@angular/fire/functions';
import { getStorage, provideStorage, connectStorageEmulator } from '@angular/fire/storage';
import { IonicStorageModule } from '@ionic/storage-angular';
import { ApmModule, ApmService } from '@elastic/apm-rum-angular';
import { getAnalytics, provideAnalytics, isSupported, ScreenTrackingService, UserTrackingService } from '@angular/fire/analytics';

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  exports: [],
  imports: [
    BrowserModule,
    IonicModule.forRoot(),
    AppRoutingModule,
    BrowserAnimationsModule,
    IonicStorageModule.forRoot(),
    // Firebase modular API only
    MappingsModule,
    AuthModule,
    ApmModule,
  ],
  providers: [
    // Removed compat emulator providers; use modular API only
    provideFirebaseApp(() =>
      initializeApp(environment.firebaseConfig, 'conversia')
    ),
    provideAuth(() => {
      const auth = getAuth(getApp('conversia'));
      if (environment.useEmulators) {
        // Firebase Auth Emulator (default port 9099)
        connectAuthEmulator(auth, 'http://localhost:9099', {
          disableWarnings: true,
        });
      }
      return auth;
    }),
    provideFunctions(() => {
      const app = getApp('conversia');
      const functions = getFunctions(app);
      if (environment.useEmulators) {
        // Cloud Functions Emulator (default port 5001)
        connectFunctionsEmulator(functions, 'localhost', 5001);
        // Also connect default-region instance to cover callers without explicit region
        const defaultFns = getFunctions(app);
        connectFunctionsEmulator(defaultFns, 'localhost', 5001);
      }
      return functions;
    }),
    provideFirestore(() => {
      const app = getApp('conversia');
      const firestore = getFirestore(app);
      // If an alternate Firestore database id is provided in the environment (e.g. 'link'),
      // initialize Firestore against that named database. Otherwise use the default DB.
      if ((environment as any).firestoreDatabase) {
        return initializeFirestore(app, {}, (environment as any).firestoreDatabase);
      }
      if (environment.useEmulators) {
        // Firestore Emulator (configured port 8081 in firebase.json)
        connectFirestoreEmulator(firestore, 'localhost', 8081);
      }
      return firestore;
    }),
    provideStorage(() => {
      const storage = getStorage(getApp('conversia'));
      if (environment.useEmulators) {
        // Cloud Storage Emulator (default port 9199)
        connectStorageEmulator(storage, 'localhost', 9199);
      }
      return storage;
    }),
    provideHttpClient(withInterceptorsFromDi()),
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    Insomnia,
    BatteryStatus,
    AndroidFullScreen,
    ApmService,
    // Automatic screen and user tracking for Analytics
    ScreenTrackingService,
    UserTrackingService,
  ],
})
export class AppModule {
  constructor(service: ApmService) {
    // Agent API is exposed through this apm instance
    const apm = service.init({
      serviceName: 'conversIA-APM',
      //serverUrl: 'http://localhost:8200',
      serverUrl:
        'https://7126729d5078440d972a19ec87129dce.apm.us-central1.gcp.cloud.es.io:443',
      environment: environment.environmentName,
      disableInstrumentations: [
        'page-load',
        'eventtarget',
        'click',
        'error',
        'history',
        'fetch',
        'xmlhttprequest',
      ],
    });
    apm.addLabels({ version: environment.version });
  }
}
